import { ArrowDown, ArrowUp, Download, FileSpreadsheet, Save, Table2, Wand2 } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";
import { FilePrepNocrm } from "@/components/fileprep/FilePrepNocrm";

const MAX_COLUMNS = 26;
const TEMPLATE_KEY = "manageo-template";

export default function FilePrepPage() {
  const [file, setFile] = useState(null);
  const [columns, setColumns] = useState([]);
  const [preview, setPreview] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dedupOn, setDedupOn] = useState(false);
  const [dedupEmail, setDedupEmail] = useState("");
  const [dedupPhone, setDedupPhone] = useState("");
  const [parts, setParts] = useState(1);
  const inputRef = useRef(null);

  const kept = columns.filter((c) => c.keep);
  const hasTemplate = Boolean(localStorage.getItem(TEMPLATE_KEY));

  const onPick = async (picked) => {
    if (!picked) return;
    setFile(picked);
    setLoading(true);
    const body = new FormData();
    body.append("file", picked);
    try {
      const { data } = await api.post("/file-prep/parse", body);
      const anyUseful = data.columns.some((c) => c.useful);
      setColumns(data.columns.map((c) => ({ index: c.index, name: c.name, rename: c.name, keep: anyUseful ? c.useful : true })));
      setPreview(data.preview);
      setRowCount(data.row_count);
      setDedupEmail(data.email_index ?? "");
      setDedupPhone(data.phone_index ?? "");
      setDedupOn(data.email_index != null || data.phone_index != null);
      if (anyUseful) {
        const n = data.columns.filter((c) => c.useful).length;
        toast.success(`${n} colonne(s) clé(s) pré-cochée(s) automatiquement. Ajustez si besoin.`);
      }
      if (data.column_count > MAX_COLUMNS && !anyUseful) toast.warning(`${data.column_count} colonnes détectées : décochez-en au moins ${data.column_count - MAX_COLUMNS} (limite noCRM : ${MAX_COLUMNS}).`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Lecture du fichier impossible.");
      setColumns([]); setPreview([]); setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  const move = (from, to) => {
    if (to < 0 || to >= columns.length) return;
    const next = [...columns];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setColumns(next);
  };
  const setCol = (i, patch) => setColumns(columns.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));

  const saveTemplate = () => {
    localStorage.setItem(TEMPLATE_KEY, JSON.stringify(columns.filter((c) => c.keep).map((c) => ({ name: c.name, rename: c.rename }))));
    toast.success("Modèle Manageo enregistré. Il s'appliquera en 1 clic aux prochains fichiers.");
  };
  const applyTemplate = () => {
    const tpl = JSON.parse(localStorage.getItem(TEMPLATE_KEY) || "[]");
    if (!tpl.length) return;
    const order = {};
    tpl.forEach((t, i) => { order[t.name] = { rename: t.rename, pos: i }; });
    const next = columns
      .map((c) => (order[c.name] ? { ...c, keep: true, rename: order[c.name].rename } : { ...c, keep: false }))
      .sort((a, b) => (order[a.name]?.pos ?? 999) - (order[b.name]?.pos ?? 999));
    setColumns(next);
    const missing = tpl.filter((t) => !columns.some((c) => c.name === t.name));
    toast.success(`Modèle appliqué : ${tpl.length - missing.length} colonne(s) conservée(s)${missing.length ? `, ${missing.length} introuvable(s) dans ce fichier` : ""}.`);
  };

  const buildConfig = () => {
    if (!kept.length) { toast.error("Sélectionnez au moins une colonne."); return null; }
    if (kept.length > MAX_COLUMNS) { toast.error(`${kept.length} colonnes : retirez-en ${kept.length - MAX_COLUMNS} (limite ${MAX_COLUMNS}).`); return null; }
    return {
      columns: kept.map((c) => ({ index: c.index, name: (c.rename || c.name).trim() })),
      filename: (file.name || "prospection").replace(/\.[^.]+$/, ""),
      dedup: { enabled: dedupOn, email_index: dedupEmail === "" ? null : Number(dedupEmail), phone_index: dedupPhone === "" ? null : Number(dedupPhone) },
    };
  };

  const doExport = async () => {
    const config = buildConfig();
    if (!config) return;
    setExporting(true);
    const body = new FormData();
    body.append("file", file);
    body.append("config", JSON.stringify({ ...config, parts: Math.max(1, Number(parts) || 1) }));
    try {
      const res = await api.post("/file-prep/export", body, { responseType: "blob" });
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : "fichier-propre.xlsx";
      const removed = Number(res.headers["x-duplicates-removed"] || 0);
      const nparts = Number(res.headers["x-parts"] || 1);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url; link.download = filename; document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      const dupMsg = dedupOn && removed > 0 ? ` ${removed} doublon(s) retiré(s).` : "";
      toast.success((nparts > 1 ? `Liste répartie en ${nparts} fichiers (ZIP) — téléchargement lancé.` : "Fichier propre téléchargé, prêt pour l'import noCRM.") + dupMsg);
    } catch (error) {
      const detail = error.response?.data instanceof Blob ? JSON.parse(await error.response.data.text()).detail : error.response?.data?.detail;
      toast.error(detail || "Export impossible.");
    } finally {
      setExporting(false);
    }
  };

  const previewCols = kept.length ? kept : columns;

  return (
    <section className="page" data-testid="file-prep-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">PRÉPARATION FICHIER</p>
          <h1>Préparer un fichier de prospection</h1>
          <p className="page-subtitle">Charge ton export Manageo (.xlsx ou .csv), choisis les colonnes à garder (≤ 26), et récupère un fichier propre prêt pour l'import dans ta liste de prospection noCRM. Aucune opportunité n'est créée.</p>
        </div>
      </header>

      <section className="settings-section" data-testid="file-prep-drop">
        <input ref={inputRef} type="file" accept=".xlsx,.csv,.txt" style={{ display: "none" }} onChange={(e) => onPick(e.target.files?.[0])} data-testid="file-prep-input" />
        <div className="fileprep-drop" onClick={() => inputRef.current?.click()} data-testid="file-prep-dropzone">
          <FileSpreadsheet size={30} />
          <p><strong>{file ? file.name : "Cliquer pour choisir un fichier Manageo"}</strong></p>
          <span>{loading ? "Lecture en cours…" : file ? `${rowCount.toLocaleString("fr-FR")} lignes · ${columns.length} colonnes` : ".xlsx ou .csv"}</span>
        </div>
      </section>

      {columns.length > 0 && (
        <>
          <section className="settings-section" data-testid="file-prep-columns">
            <div className="section-title">
              <Table2 size={20} />
              <div><p className="eyebrow">COLONNES</p><h2>Choisir, réordonner et renommer</h2></div>
              <div className="fileprep-counter-wrap">
                <span className={`fileprep-counter ${kept.length > MAX_COLUMNS ? "over" : ""}`} data-testid="file-prep-counter">{kept.length} / {MAX_COLUMNS} colonnes</span>
              </div>
            </div>
            <div className="button-row" style={{ padding: "0 4px 12px", gap: 8, flexWrap: "wrap" }}>
              <button type="button" className="secondary-button" onClick={() => setColumns(columns.map((c) => ({ ...c, keep: true })))} data-testid="file-prep-selectall">Tout garder</button>
              <button type="button" className="secondary-button" onClick={() => setColumns(columns.map((c) => ({ ...c, keep: false })))} data-testid="file-prep-clear">Tout décocher</button>
              <span style={{ flex: 1 }} />
              <button type="button" className="secondary-button" onClick={saveTemplate} data-testid="file-prep-save-template"><Save size={14} /> Enregistrer le modèle</button>
              {hasTemplate && <button type="button" className="primary-button" onClick={applyTemplate} data-testid="file-prep-apply-template"><Wand2 size={14} /> Appliquer le modèle Manageo</button>}
            </div>
            <div className="fileprep-col-list">
              {columns.map((c, i) => (
                <div key={`${c.index}-${i}`} className={`fileprep-col ${c.keep ? "kept" : ""}`} data-testid={`file-prep-col-${c.index}`}>
                  <div className="fileprep-move">
                    <button type="button" onClick={() => move(i, i - 1)} disabled={i === 0} aria-label="Monter" data-testid={`file-prep-up-${c.index}`}><ArrowUp size={14} /></button>
                    <button type="button" onClick={() => move(i, i + 1)} disabled={i === columns.length - 1} aria-label="Descendre" data-testid={`file-prep-down-${c.index}`}><ArrowDown size={14} /></button>
                  </div>
                  <label className="fileprep-keep"><input type="checkbox" checked={c.keep} onChange={(e) => setCol(i, { keep: e.target.checked })} data-testid={`file-prep-keep-${c.index}`} /></label>
                  <span className="fileprep-orig" title={c.name}>{c.name || <em>(sans nom)</em>}</span>
                  <input className="fileprep-rename" value={c.rename} onChange={(e) => setCol(i, { rename: e.target.value })} disabled={!c.keep} placeholder="Renommer…" data-testid={`file-prep-rename-${c.index}`} />
                </div>
              ))}
            </div>
          </section>

          <section className="settings-section" data-testid="file-prep-preview">
            <div className="section-title"><Table2 size={20} /><div><p className="eyebrow">APERÇU</p><h2>5 premières lignes (colonnes conservées)</h2></div></div>
            <div className="agent-table-wrap">
              <table className="agent-table" data-testid="file-prep-preview-table">
                <thead><tr>{previewCols.map((c) => <th key={c.index}>{c.rename || c.name}</th>)}</tr></thead>
                <tbody>
                  {preview.map((row, r) => (
                    <tr key={r}>{previewCols.map((c) => <td key={c.index} className="agent-name-cell">{row[c.index] ?? ""}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="settings-section" data-testid="file-prep-dedup">
            <div className="section-title">
              <Table2 size={20} />
              <div><p className="eyebrow">DÉDOUBLONNAGE</p><h2>Retirer les doublons avant l'export</h2></div>
              <label className="fileprep-dedup-switch">
                <input type="checkbox" checked={dedupOn} onChange={(e) => setDedupOn(e.target.checked)} data-testid="file-prep-dedup-toggle" />
                <span>Activer</span>
              </label>
            </div>
            <p className="fileprep-dedup-hint">Une ligne est considérée comme doublon si son <strong>e-mail</strong> OU son <strong>téléphone</strong> a déjà été vu plus haut. La première occurrence est conservée.</p>
            <div className="fileprep-dedup-row">
              <label>
                <span>Colonne e-mail</span>
                <select value={dedupEmail} onChange={(e) => setDedupEmail(e.target.value)} disabled={!dedupOn} data-testid="file-prep-dedup-email">
                  <option value="">— Aucune —</option>
                  {columns.map((c) => <option key={c.index} value={c.index}>{c.name}</option>)}
                </select>
              </label>
              <label>
                <span>Colonne téléphone</span>
                <select value={dedupPhone} onChange={(e) => setDedupPhone(e.target.value)} disabled={!dedupOn} data-testid="file-prep-dedup-phone">
                  <option value="">— Aucune —</option>
                  {columns.map((c) => <option key={c.index} value={c.index}>{c.name}</option>)}
                </select>
              </label>
            </div>
          </section>

          <section className="settings-section" data-testid="file-prep-split">
            <div className="section-title">
              <Table2 size={20} />
              <div><p className="eyebrow">DÉCOUPAGE</p><h2>Répartir en plusieurs fichiers</h2></div>
            </div>
            <div className="fileprep-split-row">
              <label>
                <span>Nombre de fichiers (portions)</span>
                <input type="number" min="1" max="99" value={parts} onChange={(e) => setParts(Math.max(1, Math.min(99, Number(e.target.value) || 1)))} data-testid="file-prep-parts" />
              </label>
              <div className="fileprep-split-info" data-testid="file-prep-split-info">
                {(() => {
                  const minParts = Math.max(1, Math.ceil(rowCount / 5000));
                  const eff = Math.max(Number(parts) || 1, minParts);
                  const per = Math.ceil(rowCount / eff);
                  return (
                    <>
                      <strong>{eff} fichier(s)</strong> · ≈ {per.toLocaleString("fr-FR")} contacts chacun
                      {eff > (Number(parts) || 1) && <em className="fileprep-split-note"> — ajusté à {eff} pour respecter la limite noCRM de 5 000 lignes/fichier</em>}
                    </>
                  );
                })()}
              </div>
            </div>
            <p className="fileprep-dedup-hint">Choisis en combien de fichiers répartir la liste (ex. 3 fichiers pour 3 commerciaux). L'outil équilibre le nombre de contacts par fichier. Maximum 5 000 lignes par fichier (limite noCRM).</p>
          </section>

          <FilePrepNocrm count={rowCount} buildConfig={buildConfig} file={file} />

          <div className="button-row" style={{ marginTop: 8 }}>
            <button type="button" className="primary-button" disabled={exporting || !kept.length || kept.length > MAX_COLUMNS} onClick={doExport} data-testid="file-prep-export">
              <Download size={16} /> {exporting ? "Génération…" : (() => { const eff = Math.max(Number(parts) || 1, Math.max(1, Math.ceil(rowCount / 5000))); return eff > 1 ? `Télécharger (${eff} fichiers)` : "Télécharger le fichier propre"; })()}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
